import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X, Check, ArrowRight, User, FolderKanban, CreditCard } from 'lucide-react'
import { useConvertLead } from '../../api/leads'
import { useCreateClient } from '../../api/clients'
import { useCreateProject } from '../../api/projects'
import { useCreatePayment } from '../../api/payments'
import { useStages } from '../../api/projects'
import type { ProjectPriority } from '@1130-studio/shared'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { cn } from '../../lib/utils'
import type { Lead, ClientType, PaymentType } from '@1130-studio/shared'

interface ConvertWizardModalProps {
  lead: Lead
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

type Step = 'client' | 'project' | 'payment'

const steps: { id: Step; label: string; icon: React.ReactNode }[] = [
  { id: 'client', label: 'Клиент', icon: <User className="w-4 h-4" /> },
  { id: 'project', label: 'Проект', icon: <FolderKanban className="w-4 h-4" /> },
  { id: 'payment', label: 'Платёж', icon: <CreditCard className="w-4 h-4" /> },
]

export default function ConvertWizardModal({ lead, isOpen, onClose, onSuccess }: ConvertWizardModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>('client')
  const [createdClientId, setCreatedClientId] = useState<string | null>(null)
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Client form
  const [clientData, setClientData] = useState({
    type: 'individual' as ClientType,
    contactName: lead.name,
    companyName: '',
    email: lead.email || '',
    phone: lead.phone || '',
    telegram: lead.telegram || '',
  })

  // Project form
  const [projectData, setProjectData] = useState({
    name: `Проект для ${lead.name}`,
    description: lead.specification || '',
    priority: 'medium' as ProjectPriority,
    budget: '',
  })

  // Payment form
  const [paymentData, setPaymentData] = useState({
    name: 'Предоплата',
    amount: '',
    type: 'prepayment' as PaymentType,
    skipPayment: false,
  })

  const convertLead = useConvertLead()
  const createClient = useCreateClient()
  const createProject = useCreateProject()
  const createPayment = useCreatePayment()
  const { data: stagesData } = useStages()

  const isProcessing = convertLead.isPending || createClient.isPending || createProject.isPending || createPayment.isPending

  const handleCreateClient = async () => {
    setError(null)
    try {
      // First convert the lead
      await convertLead.mutateAsync(lead.id)

      // Then create client
      const client = await createClient.mutateAsync({
        type: clientData.type,
        contactName: clientData.contactName,
        companyName: clientData.type === 'company' ? clientData.companyName : undefined,
        email: clientData.email || undefined,
        phone: clientData.phone || undefined,
        telegram: clientData.telegram || undefined,
        leadId: lead.id,
      })

      setCreatedClientId(client.id)
      setCurrentStep('project')
    } catch (err: any) {
      setError(err.message || 'Ошибка создания клиента')
    }
  }

  const handleCreateProject = async () => {
    setError(null)
    if (!createdClientId) return

    try {
      // Find first project stage
      const projectStage = stagesData?.find((s) => s.type === 'project')

      const project = await createProject.mutateAsync({
        name: projectData.name,
        description: projectData.description || undefined,
        clientId: createdClientId,
        leadId: lead.id,
        stageId: projectStage?.id || undefined,
        priority: projectData.priority,
        budget: projectData.budget || undefined,
      })

      setCreatedProjectId(project.id)
      setCurrentStep('payment')
    } catch (err: any) {
      setError(err.message || 'Ошибка создания проекта')
    }
  }

  const handleCreatePayment = async () => {
    setError(null)
    if (!createdProjectId) return

    if (paymentData.skipPayment) {
      onSuccess()
      return
    }

    if (!paymentData.amount) {
      setError('Укажите сумму платежа')
      return
    }

    try {
      await createPayment.mutateAsync({
        projectId: createdProjectId,
        name: paymentData.name,
        amount: paymentData.amount,
        type: paymentData.type,
        status: 'pending',
      })

      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Ошибка создания платежа')
    }
  }

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep)

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative bg-bg-card border border-border-medium backdrop-blur-glass w-full max-w-lg rounded-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-lg font-bold text-white">Конвертация заявки</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Steps indicator */}
            <div className="flex items-center gap-1 px-6 pt-5">
              {steps.map((step, idx) => (
                <div key={step.id} className="flex items-center flex-1">
                  <div
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                      idx < currentStepIndex
                        ? 'bg-accent-lime/20 text-accent-lime'
                        : idx === currentStepIndex
                          ? 'bg-white/10 text-white'
                          : 'bg-white/5 text-text-tertiary'
                    )}
                  >
                    {idx < currentStepIndex ? <Check className="w-3.5 h-3.5" /> : step.icon}
                    {step.label}
                  </div>
                  {idx < steps.length - 1 && (
                    <ArrowRight className="w-3.5 h-3.5 text-text-tertiary mx-1 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {error}
                </div>
              )}

              {/* Step 1: Client */}
              {currentStep === 'client' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Тип клиента</label>
                    <div className="flex gap-2">
                      {(['individual', 'company'] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => setClientData((d) => ({ ...d, type: t }))}
                          className={cn(
                            'flex-1 px-3 py-2 rounded-lg text-sm border transition-colors',
                            clientData.type === t
                              ? 'bg-white/10 text-white border-white/20'
                              : 'bg-white/5 text-text-tertiary border-white/10 hover:text-white'
                          )}
                        >
                          {t === 'individual' ? 'Физ. лицо' : 'Юр. лицо'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Input
                    label="Контактное лицо *"
                    value={clientData.contactName}
                    onChange={(e) => setClientData((d) => ({ ...d, contactName: e.target.value }))}
                  />

                  {clientData.type === 'company' && (
                    <Input
                      label="Название компании"
                      value={clientData.companyName}
                      onChange={(e) => setClientData((d) => ({ ...d, companyName: e.target.value }))}
                    />
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Email"
                      value={clientData.email}
                      onChange={(e) => setClientData((d) => ({ ...d, email: e.target.value }))}
                    />
                    <Input
                      label="Телефон"
                      value={clientData.phone}
                      onChange={(e) => setClientData((d) => ({ ...d, phone: e.target.value }))}
                    />
                  </div>

                  <Input
                    label="Telegram"
                    value={clientData.telegram}
                    onChange={(e) => setClientData((d) => ({ ...d, telegram: e.target.value }))}
                  />
                </div>
              )}

              {/* Step 2: Project */}
              {currentStep === 'project' && (
                <div className="space-y-4">
                  <Input
                    label="Название проекта *"
                    value={projectData.name}
                    onChange={(e) => setProjectData((d) => ({ ...d, name: e.target.value }))}
                  />

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Описание</label>
                    <textarea
                      value={projectData.description}
                      onChange={(e) => setProjectData((d) => ({ ...d, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 resize-none"
                      placeholder="Описание проекта..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1.5">Приоритет</label>
                      <select
                        value={projectData.priority}
                        onChange={(e) => setProjectData((d) => ({ ...d, priority: e.target.value as ProjectPriority }))}
                        className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                      >
                        <option value="low">Низкий</option>
                        <option value="medium">Средний</option>
                        <option value="high">Высокий</option>
                        <option value="urgent">Срочный</option>
                      </select>
                    </div>
                    <Input
                      label="Бюджет"
                      value={projectData.budget}
                      onChange={(e) => setProjectData((d) => ({ ...d, budget: e.target.value }))}
                      placeholder="100000"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Payment */}
              {currentStep === 'payment' && (
                <div className="space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentData.skipPayment}
                      onChange={(e) => setPaymentData((d) => ({ ...d, skipPayment: e.target.checked }))}
                      className="w-4 h-4 rounded border-border-medium bg-bg-tertiary text-accent-lime"
                    />
                    <span className="text-sm text-text-secondary">Пропустить создание платежа</span>
                  </label>

                  {!paymentData.skipPayment && (
                    <>
                      <Input
                        label="Название платежа *"
                        value={paymentData.name}
                        onChange={(e) => setPaymentData((d) => ({ ...d, name: e.target.value }))}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label="Сумма *"
                          value={paymentData.amount}
                          onChange={(e) => setPaymentData((d) => ({ ...d, amount: e.target.value }))}
                          placeholder="50000"
                        />
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-1.5">Тип</label>
                          <select
                            value={paymentData.type}
                            onChange={(e) => setPaymentData((d) => ({ ...d, type: e.target.value as PaymentType }))}
                            className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                          >
                            <option value="prepayment">Предоплата</option>
                            <option value="milestone">Этап</option>
                            <option value="final">Финальный</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between p-6 border-t border-white/10">
              <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
                Отмена
              </Button>

              {currentStep === 'client' && (
                <Button
                  variant="primary"
                  onClick={handleCreateClient}
                  disabled={isProcessing || !clientData.contactName.trim()}
                  loading={convertLead.isPending || createClient.isPending}
                >
                  Создать клиента
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}

              {currentStep === 'project' && (
                <Button
                  variant="primary"
                  onClick={handleCreateProject}
                  disabled={isProcessing || !projectData.name.trim()}
                  loading={createProject.isPending}
                >
                  Создать проект
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}

              {currentStep === 'payment' && (
                <Button
                  variant="primary"
                  onClick={handleCreatePayment}
                  disabled={isProcessing || (!paymentData.skipPayment && !paymentData.amount)}
                  loading={createPayment.isPending}
                >
                  {paymentData.skipPayment ? 'Завершить' : 'Создать платёж'}
                  <Check className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
